import { showFullpageLoader } from '@bridebook/web/app-basic/lib/ab-tests/ab-tests-slice';
import { getLoaderVisible } from '@bridebook/web/app-basic/lib/ab-tests/selectors';
import { basicWrapper } from '@bridebook/web/app-basic/lib/basic-store';
import { addI18nHeaderToFetch } from '@bridebook/web/app-basic/lib/i18n/utils/add-i18n-header-to-fetch';
import { mergeI18nTranslations } from '@bridebook/web/app-basic/lib/i18n/utils/merge-i18n-translations';
import { LayoutBasic } from '@bridebook/web/app-basic/lib/layout-basic';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { appWithTranslation } from 'next-i18next';
import { ComponentType, ReactElement, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { compose } from 'redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { getQueryClient } from '@bridebook/web/store/react-query-client';
import FullpageLoader from '@bridebook/web/app-shared/components/fullpage-loader/fullpage-loader';
import { IAppProps } from '@bridebook/web/pages/_app';
import { useSetMarketCookie } from '@bridebook/web/lib/auth/hooks/use-set-market-cookie-listener';
import nextI18NextConfig from '../next-i18next.app-basic.config';

addI18nHeaderToFetch();

/**
 * Full page loader covers the page until AB tests are loaded
 */
const LoaderOverlay = () => {
  const loaderVisible = useSelector(getLoaderVisible);
  const dispatch = useDispatch();

  // Loader only covers the server-side rendered page and hides when client is initialized.
  // This is in case a client displays a different variant of the page.
  useEffect(() => {
    dispatch(showFullpageLoader(false));
  }, [dispatch]);

  return loaderVisible ? <FullpageLoader /> : null;
};

const AppBasic = (props: IAppProps) => {
  const { Component, pageProps, router, ...rest } = props;
  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);

  // Per-request on the server, singleton in the browser
  const queryClient = getQueryClient();

  useSetMarketCookie();

  return (
    <QueryClientProvider client={queryClient}>
      <LayoutBasic router={router}>
        <LoaderOverlay />
        {getLayout(<Component {...pageProps} {...rest} />)}
      </LayoutBasic>
    </QueryClientProvider>
  );
};

export default compose<ComponentType<IAppProps>>(
  mergeI18nTranslations,
  basicWrapper.withRedux,
  (Comp: IAppProps['Component']) => appWithTranslation(Comp, nextI18NextConfig),
)(AppBasic);
